import React, { useEffect, useState } from "react";
import Slider from "react-slick";
import axiosClient from "../../axios-client";
import { FaStar } from "react-icons/fa";

// Default fallback testimonials shown if database has no reviews yet
const defaultFallbackReviews = [
  {
    title: "Comprehensive Market Analysis",
    reviewText:
      "Bizwit Insights provided invaluable market intelligence that helped us make strategic expansion decisions with confidence. Truly top-tier research.",
    rating: 5,
    userId: { name: "Sarah Jenkins, VP Strategy" },
  },
  {
    title: "Accurate Forecasts & Deep Insights",
    reviewText:
      "The detailed reports and granular forecasting gave our team a solid competitive advantage. Their analyst support was swift and thorough.",
    rating: 5,
    userId: { name: "David Chen, Lead Market Analyst" },
  },
  {
    title: "Exceptional Research Quality",
    reviewText:
      "Outstanding industry reports with actionable data and trend forecasting that saved our consulting team months of primary research.",
    rating: 5,
    userId: { name: "Elena Rostova, Research Head" },
  },
];

const CustomerReviews = () => {
  const [reviews, setReviews] = useState([]);

  useEffect(() => {
    const fetchReviews = async () => {
      try {
        const response = await axiosClient.get("/reviews/allreviews");
        const fetchedReviews = Array.isArray(response.data) ? response.data : [];
        setReviews(fetchedReviews);
      } catch (error) {
        console.error("Error fetching reviews:", error);
        setReviews([]);
      }
    };

    fetchReviews();
  }, []);

  // Use dynamic reviews from DB if available, otherwise fallback to default reviews
  const displayReviews = reviews.length > 0 ? reviews : defaultFallbackReviews;

  const settings = {
    dots: true,
    infinite: displayReviews.length > 3,
    speed: 500,
    slidesToShow: Math.min(3, Math.max(1, displayReviews.length)),
    slidesToScroll: 1,
    responsive: [
      {
        breakpoint: 1280, // Laptops
        settings: {
          slidesToShow: Math.min(2, Math.max(1, displayReviews.length)),
          slidesToScroll: 1,
          infinite: displayReviews.length > 2,
        },
      },
      {
        breakpoint: 1024, // Tablets
        settings: {
          slidesToShow: Math.min(2, Math.max(1, displayReviews.length)),
          slidesToScroll: 1,
          infinite: displayReviews.length > 2,
        },
      },
      {
        breakpoint: 640, // Mobile
        settings: {
          slidesToShow: 1,
          slidesToScroll: 1,
          infinite: displayReviews.length > 1,
        },
      },
    ],
  };

  return (
    <div className="py-20 mb-20">
      <div className="max-w-7xl mx-auto px-6">
        <h2 className="lg:text-4xl md:text-3xl text-2xl font-bold text-center mb-8 font-urban">
          Customer Reviews
        </h2>

        {/* Carousel Section */}
        {displayReviews.length > 0 ? (
          <Slider {...settings}>
            {displayReviews.map((review, index) => (
              <div key={index} className="p-4">
                <div className="bg-white p-6 rounded-xl shadow-lg border border-gray-100 font-manrope text-left h-full flex flex-col justify-between">
                  <div>
                    <h3 className="text-xl font-bold mb-2 text-gray-800">
                      {review.title || "Market Research"}
                    </h3>

                    <p className="font10 mb-4 lg:text-base text-sm text-gray-600 leading-relaxed">
                      {review.reviewText || "No review text provided."}
                    </p>
                  </div>

                  <div className="flex items-center justify-between pt-4 border-t border-gray-100">
                    <span className="font11 font-semibold text-sm lg:text-base text-gray-900">
                      {review.userId?.name || review.name || "Verified Client"}
                    </span>
                    <div className="flex text-yellow-400">
                      {Array.from({ length: review.rating || 5 }).map((_, i) => (
                        <FaStar
                          key={`filled-${i}`}
                          className="lg:w-4 lg:h-4 w-3.5 h-3.5 mr-0.5"
                        />
                      ))}
                      {Array.from({
                        length: Math.max(0, 5 - (review.rating || 5)),
                      }).map((_, i) => (
                        <FaStar
                          key={`empty-${i}`}
                          className="lg:w-4 lg:h-4 w-3.5 h-3.5 mr-0.5 text-gray-300"
                        />
                      ))}
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </Slider>
        ) : (
          <div className="text-center py-12 text-gray-500 font-poppins">
            <p className="text-lg">No reviews available yet.</p>
          </div>
        )}
      </div>
    </div>
  );
};

export default CustomerReviews;
